import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Contract,
  Employee,
  Task,
  WorkType,
  deleteTask,
  getContracts,
  getEmployees,
  getTasks,
  getWorkTypes,
} from '../services/api';
import { SortState, compareValues, formatContractSumDisplay, formatDateShort, toggleSort } from '../utils/table';
import {
  TASK_STATUS_FILTER_OPTIONS,
  getTaskStatusLabel,
  normalizeTaskStatus,
  type TaskLifecycleStatus,
} from '../utils/taskStatus';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import '../styles/DataPage.css';

function parseTasksSumBound(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function parseFilterDay(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const ms = new Date(`${v}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Пересечение [start задачи, end задачи] с [filterFrom, filterTo]; без end у задачи — открытый конец вправо. */
function taskOverlapsDateRange(
  taskStart: string,
  taskEnd: string | undefined | null,
  filterFrom: string,
  filterTo: string,
): boolean {
  const hasFrom = filterFrom.trim() !== '';
  const hasTo = filterTo.trim() !== '';
  if (!hasFrom && !hasTo) return true;

  const ts = parseFilterDay(taskStart);
  if (ts === null) return true;

  let rangeStart = hasFrom ? parseFilterDay(filterFrom) : Number.NEGATIVE_INFINITY;
  let rangeEnd = hasTo ? parseFilterDay(filterTo) : Number.POSITIVE_INFINITY;
  if (rangeStart === null) rangeStart = Number.NEGATIVE_INFINITY;
  if (rangeEnd === null) rangeEnd = Number.POSITIVE_INFINITY;

  const te = taskEnd?.trim() ? parseFilterDay(taskEnd) : null;
  const taskEndCmp = te === null ? Number.POSITIVE_INFINITY : te;

  return ts <= rangeEnd && rangeStart <= taskEndCmp;
}

function taskDetailPath(task: Pick<Task, 'id' | 'contracts_task_id'>): string {
  return `/tasks/${task.id}?contracts_task_id=${task.contracts_task_id}`;
}

type TaskSortKey =
  | 'contract'
  | 'task_id'
  | 'short_name'
  | 'manager'
  | 'project_lead'
  | 'start_date'
  | 'end_date'
  | 'task_status'
  | 'work_type'
  | 'amount';

type TasksFilterMenuKey = 'name' | 'contract' | 'work_type' | 'manager' | 'project_lead' | 'status' | null;

const TasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { can, hasDepartment } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [employeesList, setEmployeesList] = useState<Employee[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedContractsTaskId, setSelectedContractsTaskId] = useState<number | null>(null);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<TasksFilterMenuKey>(null);

  const [searchText, setSearchText] = useState('');
  const [taskIdFilter, setTaskIdFilter] = useState('');
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const [filterWorkTypes, setFilterWorkTypes] = useState<string[]>([]);
  const [filterContractNumbers, setFilterContractNumbers] = useState<string[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterTaskStatuses, setFilterTaskStatuses] = useState<TaskLifecycleStatus[]>([]);
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');
  const [filterManagers, setFilterManagers] = useState<string[]>([]);
  const [filterProjectLeads, setFilterProjectLeads] = useState<string[]>([]);

  const [sortState, setSortState] = useState<SortState<TaskSortKey>>({
    key: 'task_id',
    direction: 'desc',
  });

  const canOpenPage = hasDepartment('ALL_COMP');
  const canDelete = can('TASKS', 'DELETE');

  const workTypeNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const wt of workTypes) {
      map.set(wt.id, wt.name);
    }
    return map;
  }, [workTypes]);

  const workTypeLabel = useCallback(
    (id: number | null | undefined): string => {
      if (id == null) return '—';
      return workTypeNameById.get(id) ?? '—';
    },
    [workTypeNameById],
  );

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canOpenPage) {
      setIsLoading(false);
      setTasks([]);
      return;
    }

    setIsLoading(true);
    try {
      const [tasksData, contractsData, workTypesData] = await Promise.all([
        getTasks(),
        getContracts(),
        getWorkTypes(),
      ]);
      setTasks(tasksData);
      setContracts(contractsData);
      setWorkTypes(workTypesData);
      try {
        const emps = await getEmployees();
        setEmployeesList(emps);
      } catch {
        setEmployeesList([]);
      }
    } catch (err) {
      setError('Ошибка при загрузке данных');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [canOpenPage]);

  useEffect(() => {
    if (canOpenPage) {
      void fetchData();
    } else {
      setIsLoading(false);
      setTasks([]);
    }
  }, [canOpenPage, fetchData]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const getContractNumber = useCallback(
    (id: number): string => contracts.find((c) => c.id === id)?.contract_number || 'Неизвестно',
    [contracts],
  );

  const getEmployeeName = useCallback((id?: number | null): string => {
    if (id == null) {
      return '—';
    }
    return employeesList.find((e) => e.id === id)?.full_name || '—';
  }, [employeesList]);

  const contractFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of contracts) {
      set.add(c.contract_number);
    }
    for (const t of tasks) {
      set.add(getContractNumber(t.contract_id));
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));
  }, [contracts, tasks, getContractNumber]);

  const nameFilterOptions = useMemo(() => {
    const set = new Set(tasks.map((t) => t.short_name));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [tasks]);

  const workTypeFilterOptions = useMemo(() => {
    return workTypes.map((wt) => wt.name).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [workTypes]);

  const managerFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of tasks) {
      set.add(getEmployeeName(t.manager_id));
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [tasks, getEmployeeName]);

  const projectLeadFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of tasks) {
      set.add(getEmployeeName(t.project_lead_id));
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [tasks, getEmployeeName]);

  const clearFilters = (): void => {
    setSearchText('');
    setTaskIdFilter('');
    setFilterNames([]);
    setFilterWorkTypes([]);
    setFilterContractNumbers([]);
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterTaskStatuses([]);
    setMinAmountFilter('');
    setMaxAmountFilter('');
    setFilterManagers([]);
    setFilterProjectLeads([]);
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    taskIdFilter.trim() !== '' ||
    filterNames.length > 0 ||
    filterWorkTypes.length > 0 ||
    filterContractNumbers.length > 0 ||
    filterManagers.length > 0 ||
    filterProjectLeads.length > 0 ||
    filterTaskStatuses.length > 0 ||
    filterDateFrom.trim() !== '' ||
    filterDateTo.trim() !== '' ||
    minAmountFilter.trim() !== '' ||
    maxAmountFilter.trim() !== '';

  const filteredTasks = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    const sumFromN = parseTasksSumBound(minAmountFilter);
    const sumToN = parseTasksSumBound(maxAmountFilter);

    let effDateFrom = filterDateFrom;
    let effDateTo = filterDateTo;
    if (effDateFrom.trim() && effDateTo.trim() && effDateFrom > effDateTo) {
      const swap = effDateFrom;
      effDateFrom = effDateTo;
      effDateTo = swap;
    }

    return tasks.filter((task) => {
      const contractNumber = getContractNumber(task.contract_id);
      const managerName = getEmployeeName(task.manager_id);
      const projectLeadName = getEmployeeName(task.project_lead_id);
      const taskStatus = normalizeTaskStatus(task.task_status);
      const statusLabel = getTaskStatusLabel(taskStatus);
      const workTypeName = workTypeLabel(task.work_type_id);
      const searchable = [
        task.task_id,
        task.short_name,
        workTypeName,
        contractNumber,
        task.category_for_software,
        task.start_date,
        task.end_date,
        managerName,
        projectLeadName,
        statusLabel,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const amount = Number(task.amount) || 0;

      const matchesSearch = !text || searchable.includes(text);
      const matchesTaskId = !taskIdFilter || task.task_id.toLowerCase().includes(taskIdFilter.toLowerCase());
      const matchesName = filterNames.length === 0 || filterNames.includes(task.short_name);
      const matchesWorkType = filterWorkTypes.length === 0 || filterWorkTypes.includes(workTypeName);
      const matchesContract =
        filterContractNumbers.length === 0 || filterContractNumbers.includes(contractNumber);
      const matchesManager = filterManagers.length === 0 || filterManagers.includes(managerName);
      const matchesProjectLead =
        filterProjectLeads.length === 0 || filterProjectLeads.includes(projectLeadName);
      const matchesStatus =
        filterTaskStatuses.length === 0 || filterTaskStatuses.includes(taskStatus);
      const matchesAmount =
        (sumFromN === null || amount >= sumFromN) && (sumToN === null || amount <= sumToN);
      const matchesDateRange = taskOverlapsDateRange(
        task.start_date,
        task.end_date,
        effDateFrom,
        effDateTo,
      );

      return (
        matchesSearch &&
        matchesTaskId &&
        matchesName &&
        matchesWorkType &&
        matchesContract &&
        matchesManager &&
        matchesProjectLead &&
        matchesStatus &&
        matchesAmount &&
        matchesDateRange
      );
    });
  }, [
    tasks,
    searchText,
    taskIdFilter,
    filterNames,
    filterWorkTypes,
    filterContractNumbers,
    filterManagers,
    filterProjectLeads,
    filterTaskStatuses,
    minAmountFilter,
    maxAmountFilter,
    filterDateFrom,
    filterDateTo,
    getContractNumber,
    getEmployeeName,
    workTypeLabel,
  ]);

  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks];
    sorted.sort((left, right) => {
      const leftMap: Record<TaskSortKey, string | number | undefined> = {
        contract: getContractNumber(left.contract_id),
        task_id: left.task_id,
        short_name: left.short_name,
        manager: getEmployeeName(left.manager_id),
        project_lead: getEmployeeName(left.project_lead_id),
        start_date: left.start_date,
        end_date: left.end_date,
        task_status: getTaskStatusLabel(normalizeTaskStatus(left.task_status)),
        work_type: workTypeLabel(left.work_type_id),
        amount: left.amount,
      };

      const rightMap: Record<TaskSortKey, string | number | undefined> = {
        contract: getContractNumber(right.contract_id),
        task_id: right.task_id,
        short_name: right.short_name,
        manager: getEmployeeName(right.manager_id),
        project_lead: getEmployeeName(right.project_lead_id),
        start_date: right.start_date,
        end_date: right.end_date,
        task_status: getTaskStatusLabel(normalizeTaskStatus(right.task_status)),
        work_type: workTypeLabel(right.work_type_id),
        amount: right.amount,
      };

      return compareValues(leftMap[sortState.key], rightMap[sortState.key], sortState.direction);
    });

    return sorted;
  }, [filteredTasks, sortState, getContractNumber, getEmployeeName, workTypeLabel]);

  const totalFilteredAmount = useMemo(
    () => sortedTasks.reduce((sum, task) => sum + (Number(task.amount) || 0), 0),
    [sortedTasks],
  );

  const handleSort = (key: TaskSortKey): void => {
    setSortState((current) => toggleSort(current, key));
  };

  const selectedTask = useMemo(
    () => (selectedContractsTaskId == null
      ? null
      : tasks.find((task) => task.contracts_task_id === selectedContractsTaskId) ?? null),
    [tasks, selectedContractsTaskId],
  );

  useEffect(() => {
    if (selectedContractsTaskId == null) {
      return;
    }
    if (!sortedTasks.some((task) => task.contracts_task_id === selectedContractsTaskId)) {
      setSelectedContractsTaskId(null);
    }
  }, [sortedTasks, selectedContractsTaskId]);

  const handleDeleteSelected = async (): Promise<void> => {
    if (!selectedTask || isDeleting) {
      return;
    }

    const confirmed = window.confirm(
      `Удалить задачу «${selectedTask.task_id}» (${selectedTask.short_name})?`,
    );
    if (!confirmed) {
      return;
    }

    setIsDeleting(true);
    setError('');
    try {
      await deleteTask(selectedTask.contracts_task_id);
      setTasks((current) => current.filter(
        (task) => task.contracts_task_id !== selectedTask.contracts_task_id,
      ));
      setSelectedContractsTaskId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка при удалении задачи';
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="data-page tasks-page">
      {!canOpenPage ? (
        <div className="empty-state">Нет доступа к странице задач (требуется отдел ALL_COMP).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Задачи</h1>
            <div className="page-header-row__actions">
              {canDelete && selectedTask ? (
                <button
                  type="button"
                  className="delete-row-button"
                  onClick={() => void handleDeleteSelected()}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Удаление…' : 'Удалить'}
                </button>
              ) : null}
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen && (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <label>
                    ID задачи
                    <input value={taskIdFilter} onChange={(e) => setTaskIdFilter(e.target.value)} />
                  </label>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Вид работ"
                      options={workTypeFilterOptions}
                      selected={filterWorkTypes}
                      onSelectedChange={setFilterWorkTypes}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'work_type'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'work_type' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Название"
                      options={nameFilterOptions}
                      selected={filterNames}
                      onSelectedChange={setFilterNames}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'name'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'name' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Договор"
                      options={contractFilterOptions}
                      selected={filterContractNumbers}
                      onSelectedChange={setFilterContractNumbers}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'contract'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contract' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Менеджер"
                      options={managerFilterOptions}
                      selected={filterManagers}
                      onSelectedChange={setFilterManagers}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'manager'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'manager' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="РП"
                      options={projectLeadFilterOptions}
                      selected={filterProjectLeads}
                      onSelectedChange={setFilterProjectLeads}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'project_lead'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'project_lead' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статус"
                      options={TASK_STATUS_FILTER_OPTIONS}
                      selected={filterTaskStatuses}
                      onSelectedChange={(next) => setFilterTaskStatuses(next as TaskLifecycleStatus[])}
                      formatOption={(v) => getTaskStatusLabel(v)}
                      isOpen={filterMenuOpen === 'status'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'status' : null)}
                    />
                  </div>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Диапазон дат</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                        title="Начало диапазона"
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                        title="Конец диапазона"
                      />
                    </div>
                  </label>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Сумма</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="filters-sum-block__input"
                        placeholder="от"
                        value={minAmountFilter}
                        onChange={(e) => setMinAmountFilter(e.target.value)}
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        className="filters-sum-block__input"
                        placeholder="до"
                        value={maxAmountFilter}
                        onChange={(e) => setMaxAmountFilter(e.target.value)}
                      />
                    </div>
                  </label>
                </div>
              </aside>
            )}

            <section className="table-content">
              {isLoading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table tasks-page__table">
                      <colgroup>
                        <col className="tasks-page__colgrid tasks-page__colgrid--task-id" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--name" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--work-type" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--contract" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--person" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--person" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--date" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--date" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--status" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--sum" />
                      </colgroup>
                    <thead>
                      <tr>
                        <th
                          className="sortable tasks-page__th-task-id"
                          onClick={() => handleSort('task_id')}
                          role="button"
                        >
                          ID задачи
                        </th>
                        <th className="sortable tasks-page__th-name" onClick={() => handleSort('short_name')} role="button">
                          Название задачи
                        </th>
                        <th
                          className="sortable tasks-page__th-work-type"
                          onClick={() => handleSort('work_type')}
                          role="button"
                        >
                          Вид работ
                        </th>
                        <th
                          className="sortable tasks-page__th-contract"
                          onClick={() => handleSort('contract')}
                          role="button"
                        >
                          Договор
                        </th>
                        <th className="sortable tasks-page__th-person" onClick={() => handleSort('manager')} role="button">
                          Менеджер
                        </th>
                        <th className="sortable tasks-page__th-person" onClick={() => handleSort('project_lead')} role="button">
                          РП
                        </th>
                        <th
                          className="sortable tasks-page__th-date"
                          onClick={() => handleSort('start_date')}
                          role="button"
                        >
                          Начало
                        </th>
                        <th
                          className="sortable tasks-page__th-date"
                          onClick={() => handleSort('end_date')}
                          role="button"
                        >
                          Срок
                        </th>
                        <th
                          className="sortable tasks-page__th-status"
                          onClick={() => handleSort('task_status')}
                          role="button"
                        >
                          Статус
                        </th>
                        <th
                          className="sortable col-num tasks-page__th-sum"
                          onClick={() => handleSort('amount')}
                          role="button"
                        >
                          Сумма
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTasks.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="data-table__empty">
                            Ничего не найдено
                          </td>
                        </tr>
                      ) : (
                        sortedTasks.map((task) => (
                          <tr
                            key={task.contracts_task_id}
                            className={`clickable-row${selectedContractsTaskId === task.contracts_task_id ? ' data-table__row--selected' : ''}`}
                            onClick={(e) => {
                              if (e.ctrlKey || e.metaKey) {
                                return;
                              }
                              setSelectedContractsTaskId(task.contracts_task_id);
                            }}
                          >
                            <td className="tasks-page__td-task-id">
                              <button
                                type="button"
                                className="tasks-page__cell-link tasks-page__task-id-text"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(taskDetailPath(task));
                                }}
                              >
                                {task.task_id}
                              </button>
                            </td>
                            <td className="tasks-page__name-cell">
                              <button
                                type="button"
                                className="tasks-page__cell-link tasks-page__cell-link--name"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(taskDetailPath(task));
                                }}
                              >
                                <span className="tasks-page__name-inner">{task.short_name}</span>
                              </button>
                            </td>
                            <td className="tasks-page__td-work-type">
                              <div className="tasks-page__clamp-2">{workTypeLabel(task.work_type_id)}</div>
                            </td>
                            <td className="tasks-page__td-contract">
                              <button
                                type="button"
                                className="tasks-page__cell-link tasks-page__cell-link--block"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/contracts/${task.contract_id}`);
                                }}
                              >
                                <span className="tasks-page__clamp-2">{getContractNumber(task.contract_id)}</span>
                              </button>
                            </td>
                            <td className="tasks-page__td-person">
                              <div className="tasks-page__clamp-2">{getEmployeeName(task.manager_id)}</div>
                            </td>
                            <td className="tasks-page__td-person">
                              <div className="tasks-page__clamp-2">{getEmployeeName(task.project_lead_id)}</div>
                            </td>
                            <td className="tasks-page__td-date">{formatDateShort(task.start_date)}</td>
                            <td className="tasks-page__td-date">{task.end_date ? formatDateShort(task.end_date) : '—'}</td>
                            <td className="tasks-page__td-status">
                              {getTaskStatusLabel(normalizeTaskStatus(task.task_status))}
                            </td>
                            <td className="col-num tasks-page__td-sum">{formatContractSumDisplay(task.amount)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer tasks-page__table">
                      <colgroup>
                        <col className="tasks-page__colgrid tasks-page__colgrid--task-id" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--name" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--work-type" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--contract" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--person" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--person" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--date" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--date" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--status" />
                        <col className="tasks-page__colgrid tasks-page__colgrid--sum" />
                      </colgroup>
                      <tfoot>
                        <tr>
                          <td>Итого: {sortedTasks.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td className="col-num">{formatContractSumDisplay(totalFilteredAmount)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
      </>
      )}
    </div>
  );
};

export default TasksPage;
